// AbilityButton.tsx
import React, { useContext } from 'react';
import './AbilityButton.css';
import { SimplifiedSpell } from '../../../../../gameTypes';
import GameAssetsContext from '../../../../../context/GameAssetContext';

interface AbilityButtonProps {
  abilityKey: string;
  abilityImage: string;
  spell: SimplifiedSpell;
  isAutoAttack?: boolean;
  onClick: () => void;
}

const AbilityButton: React.FC<AbilityButtonProps> = ({
  abilityKey,
  abilityImage,
  spell,
  isAutoAttack = false,
  onClick,
}) => {
  const { gameWideAssets } = useContext(GameAssetsContext);
  const {
    remainingCooldown,
    isOnCooldown,
    pointsInSpell,
    manaCost,
    timer,
    isToggleActive,
    secondaryCDRemaining,
    secondaryCDisOnCooldown,
    currentCharges,
    maxCharges,
    suppressCooldownDisplay,
    isEchoReady,
  } = spell;
  const mana = pointsInSpell > 0 ? manaCost[pointsInSpell - 1] : 0;

  let displayCooldownText = "";
  let finalGreyedOut = false; // flag for greying out the ability image

  // If cooldown display is suppressed (e.g., Rengar empowered abilities), hide cooldown
  const shouldSuppressCooldown = suppressCooldownDisplay === true;

  // Grey out if no points in spell (except for auto attack)
  if (!isAutoAttack && pointsInSpell === 0) {
    finalGreyedOut = true;
  } else if (shouldSuppressCooldown) {
    // Cooldown display is suppressed (e.g., Rengar empowered abilities)
    // Don't show cooldown text and don't grey out the ability
    displayCooldownText = "";
    finalGreyedOut = false;
  } else {
    // If secondary cooldown info is available (i.e. charges exist)
    if (
      secondaryCDRemaining !== undefined &&
      currentCharges !== undefined &&
      maxCharges !== undefined
    ) {
      if (currentCharges >= maxCharges) {
        // At max charges: no cooldown text, image is not greyed out.
        displayCooldownText = "";
        finalGreyedOut = false;
      } else {
        if (currentCharges === 0) {
          // No charges available: show primary cooldown regardless of secondary cooldown state.
          if (isOnCooldown) {
            displayCooldownText = remainingCooldown.toFixed(1) + "s";
            finalGreyedOut = true;
          }
        } else {
          // There is at least one charge available but not max.
          if (secondaryCDisOnCooldown) {
            // Secondary cooldown is active: show its remaining time and grey out.
            displayCooldownText = secondaryCDRemaining.toFixed(1) + "s";
            finalGreyedOut = true;
          } else {
            // Secondary cooldown not active: show ability normally.
            displayCooldownText = "";
            finalGreyedOut = false;
          }
        }
      }
    } else {
      // Fallback if no secondary cooldown info is available.
      // Priority: timer > cooldown (for abilities like LeBlanc W that have both)
      if (timer !== undefined && timer > 0) {
        // Timer is active: show timer and keep ability enabled (not greyed out)
        displayCooldownText = timer.toFixed(1) + "s";
        finalGreyedOut = false;
      } else if (isOnCooldown) {
        // No timer, but on cooldown: show cooldown and grey out
        displayCooldownText = remainingCooldown.toFixed(1) + "s";
        finalGreyedOut = true;
      }
    }
  }

  const isGreyedOutClass = finalGreyedOut ? 'greyed-out' : '';
  
  // Show active state for both timer-based abilities and toggle abilities
  const isActive = (timer !== undefined && timer > 0) || (isToggleActive === true);
  
  // Show Echo-ready state (yellow border for Seraphine)
  const isEchoReadyClass = isEchoReady === true ? 'echo-ready' : '';

  return (
    <div className="ability-button-container">
      <div
        className={`ability-button ${isGreyedOutClass} ${
          isActive ? 'active-timer' : ''
        } ${isEchoReadyClass}`}
        onClick={onClick}
      >
        <img
          className="ability-img"
          src={abilityImage}
          alt={`Ability ${abilityKey}`}
        />
        <div className="ability-letter">{abilityKey.toUpperCase()}</div>
        {!isAutoAttack && (
          <>
            <div className="ability-points">{pointsInSpell}</div>
            <div className="ability-mana-cost">{mana}</div>
            {displayCooldownText && (
              <div className="ability-cooldown">{displayCooldownText}</div>
            )}
          </>
        )}
      </div>
      {!isAutoAttack && (
        <div className={`ability-extra-info ${isGreyedOutClass}`}>
          <div className="ability-base-cooldown">
            <img 
              src={gameWideAssets.abilityHasteIcon} 
              alt="Cooldown" 
              className="ability-info-icon"
            />
            {spell.cooldown.toFixed(1)}s
          </div>
          <div className="ability-range">
            <img 
              src={gameWideAssets.attackRangeIcon} 
              alt="Range" 
              className="ability-info-icon"
            />
            {spell.range === -1 ? "Global" : spell.range}
          </div>
        </div>
      )}
      {/* Test field for debugging */}
  {/*     <div className="test">
        CD: {remainingCooldown.toFixed(1)}s {isOnCooldown ? ' (CD)' : '(NO)'}  
        SCD: {secondaryCDRemaining !== undefined ? secondaryCDRemaining.toFixed(1) + "s" : 'N/A'} {secondaryCDisOnCooldown ? ' (CD)' : '(NO)'}  
        Charges: {currentCharges !== undefined ? currentCharges : 'N/A'}/{maxCharges !== undefined ? maxCharges : 'N/A'}
      </div> */}
    </div>
  );
};

export default AbilityButton;
